// app/frontend/components/ui/Stepper/L2KStepper.tsx
import { useState, useMemo, useCallback, useEffect } from "react";
import { Stepper, Group, Box, Progress } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { L2KButton } from "../Forms";
import type { StepConfig, L2KStepperProps } from "./types";

/**
 * L2KStepper - Main orchestrator component for multi-step forms
 * Handles form state integration, navigation, validation, and submission
 */
export function L2KStepper<FormValues extends Record<string, any>>({
  steps,
  form,
  onSubmit,
  validateCurrentStep,
  withProgressBar = false,
  externalActive,
  onActiveChange,
  stepProps = {},
  isSubmitting = false,
  submitText = "Complete",
  nextText = "Continue",
  backText = "Back",
  hideNavigation = false,
  allowStepClick = false,
}: L2KStepperProps<FormValues>) {
  const [internalActive, setInternalActive] = useState(0);

  // Use external active if provided, otherwise internal
  const active = externalActive ?? internalActive;
  const setActive = useCallback(
    (value: number) => {
      if (onActiveChange) {
        onActiveChange(value);
      } else {
        setInternalActive(value);
      }
    },
    [onActiveChange]
  );

  // Filter steps based on conditions
  const visibleSteps = useMemo(() => {
    return steps.filter(
      (step) => !step.condition || step.condition(form.values as FormValues)
    );
  }, [steps, form.values]);

  // Ensure active step is within bounds when steps change
  useEffect(() => {
    if (active >= visibleSteps.length && visibleSteps.length > 0) {
      setActive(visibleSteps.length - 1);
    }
  }, [visibleSteps.length, active, setActive]);

  const currentStep = visibleSteps[active];
  const isLastStep = active === visibleSteps.length - 1;
  const isFirstStep = active === 0;

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    if (visibleSteps.length <= 1) return 100;
    return ((active + 1) / visibleSteps.length) * 100;
  }, [active, visibleSteps.length]);

  const handleNext = useCallback(() => {
    if (!currentStep) return;

    // Validate current step if validator provided
    if (validateCurrentStep && !validateCurrentStep(currentStep.id)) {
      return;
    }

    if (isLastStep) {
      onSubmit(form.values as FormValues);
    } else {
      setActive(active + 1);
    }
  }, [
    currentStep,
    validateCurrentStep,
    isLastStep,
    onSubmit,
    form.values,
    active,
    setActive,
  ]);

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      setActive(active - 1);
    }
  }, [isFirstStep, active, setActive]);

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      if (!allowStepClick) return;
      if (stepIndex < active) {
        setActive(stepIndex);
      }
    },
    [allowStepClick, active, setActive]
  );

  if (!currentStep) {
    return null;
  }

  const CurrentStepComponent = currentStep.component;

  return (
    <Box>
      {/* Progress Bar */}
      {withProgressBar && (
        <Progress
          value={progressPercentage}
          size="sm"
          mb="xl"
          radius="xl"
          styles={{
            root: {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            section: {
              background: "linear-gradient(90deg, #FF9800, #FF5722)",
              transition: "width 0.3s ease",
            },
          }}
        />
      )}

      {/* Stepper Indicators */}
      <Stepper
        active={active}
        onStepClick={allowStepClick ? handleStepClick : undefined}
        size="sm"
        mb="xl"
        styles={{
          root: {
            padding: "0 16px",
          },
          step: {
            cursor: allowStepClick ? "pointer" : "default",
          },
          stepIcon: {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderColor: "rgba(255, 152, 0, 0.3)",
            color: "#B8B8B8",
            fontFamily: "'Public Sans', sans-serif",
            fontWeight: 600,
            transition: "all 0.2s ease",

            "&[data-completed]": {
              background: "linear-gradient(135deg, #FF9800, #FF5722)",
              borderColor: "transparent",
              color: "#fff",
              boxShadow: "0 0 12px rgba(255, 152, 0, 0.4)",
            },

            "&[data-progress]": {
              background: "linear-gradient(135deg, #FF9800, #FF5722)",
              borderColor: "transparent",
              color: "#fff",
              boxShadow: "0 0 16px rgba(255, 152, 0, 0.5)",
            },
          },
          stepLabel: {
            color: "#F0F0F0",
            fontFamily: "'Public Sans', sans-serif",
            fontWeight: 500,
            fontSize: "14px",

            "&[data-active]": {
              color: "#FF9800",
            },
          },
          stepDescription: {
            color: "#B8B8B8",
            fontFamily: "'Public Sans', sans-serif",
            fontSize: "12px",
          },
          separator: {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            transition: "background-color 0.3s ease",

            "&[data-active]": {
              background: "linear-gradient(90deg, #FF9800, #FF5722)",
            },
          },
        }}
      >
        {visibleSteps.map((step, index) => (
          <Stepper.Step
            key={step.id}
            label={step.title}
            description={step.description}
            icon={step.icon ? <step.icon size={18} /> : undefined}
          />
        ))}
      </Stepper>

      {/* Step Content */}
      <Box py="md">
        <CurrentStepComponent form={form as UseFormReturnType<FormValues>} {...stepProps} />
      </Box>

      {/* Navigation Buttons */}
      {!hideNavigation && (
        <Group justify="center" mt="xl" gap="md">
          {!isFirstStep && (
            <L2KButton variant="subtle" onClick={handleBack} disabled={isSubmitting}>
              {backText}
            </L2KButton>
          )}
          <L2KButton onClick={handleNext} loading={isSubmitting}>
            {isLastStep ? submitText : nextText}
          </L2KButton>
        </Group>
      )}
    </Box>
  );
}

export default L2KStepper;
